import { useEffect, useRef, useState } from "react";
import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../../../utils/listenDatabaseValue";
import { db } from "../../../lib/firebase";
import { createAiCompetitorsSession } from "../aiCompetitorsApi";
import { LaunchError } from "../../../utils/errors";
import { store } from "../../../app/store";
import { isUuidSessionId } from "../aiCompetitorsBrand";
import { AI_COMPETITORS_RTDB_ROOT } from "../aiCompetitorsConstants";

const normalizeSession = (sessionId, value = {}) => {
  const competitors = Array.isArray(value.competitors)
    ? value.competitors
    : Object.values(value.competitors || {});
  const competitorNames = Array.isArray(value.adsSummary?.competitorNames)
    ? value.adsSummary.competitorNames
    : Object.values(value.adsSummary?.competitorNames || {});
  return {
    ...value,
    sessionId,
    competitors,
    adsPages:
      value.adsPages && typeof value.adsPages === "object"
        ? value.adsPages
        : {},
    adsSummary: {
      adsCount: Number(value.adsSummary?.adsCount || 0),
      competitorCount: Number(value.adsSummary?.competitorCount || 0),
      competitorsWithAds: Number(value.adsSummary?.competitorsWithAds || 0),
      competitorNames,
    },
  };
};

export const useAiCompetitorsSessionListener = ({
  sessionId,
  language,
  isActive = true,
  fetchOnce = false,
  createIfMissing = true,
  pollMs = 0,
  onNotFound,
}) => {
  const [session, setSession] = useState(null);
  const fetchedSessionIdRef = useRef("");

  useEffect(() => {
    if (!sessionId || isUuidSessionId(sessionId)) {
      return undefined;
    }

    let cancelled = false;
    let unsubscribe = () => {};
    let pollTimer = 0;
    let inFlight = false;
    let reportedFetchError = "";

    const applySession = (value) => {
      if (cancelled || !value) return;
      setSession(normalizeSession(sessionId, value));
    };

    const fetchSession = () => {
      if (inFlight) return;
      inFlight = true;
      createAiCompetitorsSession({ sessionId, language, createIfMissing })
        .then((response) => {
          if (response?.ok && response?.data) {
            applySession(response.data);
            return;
          }
          if (!createIfMissing) {
            onNotFound?.();
          }
        })
        .catch((error) => {
          const message =
            error?.message || String(error?.code || "fetchSession");
          if (reportedFetchError === message) return;
          reportedFetchError = message;
          store.dispatch(
            LaunchError(error, false, [], {
              functionName: "fetchSession",
              capture: "exception",
              context: { sessionId },
            })
          );
        })
        .finally(() => {
          inFlight = false;
        });
    };

    if (isActive) {
      const sessionRef = ref(db, `${AI_COMPETITORS_RTDB_ROOT}/${sessionId}`);
      unsubscribe = onValue(
        sessionRef,
        (snapshot) => {
          if (!snapshot.exists()) {
            return;
          }
          applySession(snapshot.val() || {});
        },
        () => {
          // Public visitors may not have RTDB read rules.
        }
      );
    }

    if (fetchOnce && fetchedSessionIdRef.current !== sessionId) {
      fetchedSessionIdRef.current = sessionId;
      fetchSession();
    }

    if (isActive && pollMs > 0) {
      fetchSession();
      pollTimer = window.setInterval(fetchSession, pollMs);
    }

    return () => {
      cancelled = true;
      unsubscribe();
      if (pollTimer) {
        window.clearInterval(pollTimer);
      }
    };
  }, [
    createIfMissing,
    fetchOnce,
    isActive,
    language,
    onNotFound,
    pollMs,
    sessionId,
  ]);

  return session;
};
